// The user's account in this browser (identity.ts): its access code kept in `localStorage`, a new one
// the first time; another device's account taken by its code — typed, or a link with `#code=` (the QR
// code of another device), whose code leaves the address bar at once.
import { code as codeOf, freshSecret, keyOf, parseCode } from "./identity.ts";
import { reconnect, send } from "./net.ts";
import { changed, show, state } from "./store.ts";

/** What the user is asked before this browser takes another account. */
const SWITCH =
  "Войти в аккаунт по этому коду доступа? Аккаунт этого браузера сменится на него — сохраните код нынешнего, если он ещё нужен.";

export function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function keep(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A private window, say: the account lives as long as the page.
  }
}

/** Who the user is: the account kept in this browser, or a new one; the name others see ("": none, a
 * guest in each meeting). A link with `#code=` brings another device's account, if the user says so. */
export async function identity(): Promise<void> {
  const kept = await parseCode(stored("xq.code") ?? "");
  await useAccount(kept ?? freshSecret());
  if (kept === null) keep("xq.code", state.code);
  state.name = stored("xq.name") ?? "";
  const given = new URLSearchParams(location.hash.slice(1)).get("code");
  if (given === null) return;
  // Off the address bar and the history at once.
  history.replaceState(null, "", location.pathname + location.search);
  const secret = await parseCode(given);
  if (secret === null || (await codeOf(secret)) === state.code) return;
  if (confirm(SWITCH)) await takeAccount(secret);
}

/** The account of `secret` is the one the page signs in as. */
async function useAccount(secret: Uint8Array): Promise<void> {
  state.secret = secret;
  state.code = await codeOf(secret);
  state.key = await keyOf(secret);
}

/** The account of `secret` (from another device) is this browser's from now on: kept, its name and
 * meetings from the server. */
async function takeAccount(secret: Uint8Array): Promise<void> {
  await useAccount(secret);
  keep("xq.code", state.code);
  state.name = "";
  keep("xq.name", "");
  state.mine = [];
  changed();
}

/** The account of access code `text` — another device's — is this browser's, if the user says so; the
 * page signs in again as it. */
export async function signInWith(text: string): Promise<void> {
  const secret = await parseCode(text);
  if (secret === null) return show("это не код доступа, или в нём опечатка", true);
  if (secret.join() === state.secret.join()) return show("это код этого же аккаунта");
  if (!confirm(SWITCH)) return;
  await takeAccount(secret);
  reconnect();
  show("вход по коду доступа…");
}

/** The name others see from now on ("": none). */
export function rename(name: string): void {
  if (name === state.name) return;
  state.name = name;
  keep("xq.name", name);
  send({ type: "name", name });
  changed();
}

/** The server's word on the account: its name, which this browser takes if it has none (the account
 * came from another device). */
export function welcomed(name: string): void {
  if (state.name !== "" || name === "") return;
  state.name = name;
  keep("xq.name", name);
  changed();
}
